/**
 * TanStack Query Client - User-Isolated Caching with Versioning
 * 
 * SECURITY: Each user gets their own cache namespace to prevent data leakage
 * VERSIONING: Cache version allows clean invalidation on breaking changes
 */

import { QueryClient } from '@tanstack/react-query';
import { get, set, del, keys } from 'idb-keyval';

// ============== CACHE VERSION ==============
const CACHE_VERSION = 'v1';  // Increment this on breaking changes

// ============== SIMPLE DEBUG LOGGER ==============
const CacheDebugger = {
    stats: {
        hits: 0,
        misses: 0,
        timeSaved: 0,
    },

    hit: (name) => {
        CacheDebugger.stats.hits++;
        CacheDebugger.stats.timeSaved += 1;
        
    },

    fetch: (name, count) => {
        CacheDebugger.stats.misses++;
        
    },

    saved: () => {
        // Silenced to keep console clean
    },

    restored: () => {
    },

    summary: () => {
        const { hits, misses, timeSaved } = CacheDebugger.stats;
        const total = hits + misses;
        const rate = total > 0 ? Math.round((hits / total) * 100) : 0;

        // console.log(`\n%c-----------------------------------------`, 'color: #64748b;');
        // console.log(`%c  CACHE SUMMARY`, 'color: #8b5cf6; font-weight: bold; font-size: 13px;');
        // console.log(`%c-----------------------------------------`, 'color: #64748b;');
        // console.log(`%c  Cache Hits:   ${hits}`, 'color: #22c55e;');
        // console.log(`%c  API Fetches:  ${misses}`, 'color: #3b82f6;');
        // console.log(`%c  Hit Rate:     ${rate}%`, 'color: #f59e0b;');
        // console.log(`%c  Time Saved:   ~${timeSaved}s`, 'color: #22c55e;');
        // console.log(`%c-----------------------------------------\n`, 'color: #64748b;');
    },

    reset: () => {
        CacheDebugger.stats = { hits: 0, misses: 0, timeSaved: 0 };
    }
};

export { CacheDebugger };

// ============== USER-SPECIFIC CACHE KEYS ==============
const getCacheKey = (userId) => {
    if (!userId) return null;
    return `vrtta-cache-${CACHE_VERSION}-user-${userId}`;
};

// Get current user ID from localStorage
const getCurrentUserId = () => {
    try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;
        const user = JSON.parse(userStr);
        return user?.id || null;
    } catch (e) {
        return null;
    }
};

// ============== INDEXEDDB STORAGE (USER-ISOLATED) ==============
export const idbPersister = {
    persistClient: async (client) => {
        try {
            const userId = getCurrentUserId();
            const cacheKey = getCacheKey(userId);
            if (!cacheKey) return; // No user, don't persist

            await set(cacheKey, client);
            CacheDebugger.saved();
        } catch (e) {
            console.error('Failed to persist cache:', e);
        }
    },

    restoreClient: async () => {
        try {
            const userId = getCurrentUserId();
            const cacheKey = getCacheKey(userId);
            if (!cacheKey) return undefined;

            const data = await get(cacheKey);
            if (data) {
                CacheDebugger.restored();
                
            }
            return data;
        } catch (e) {
            console.error('Failed to restore cache:', e);
            return undefined;
        }
    },

    removeClient: async () => {
        try {
            const userId = getCurrentUserId();
            const cacheKey = getCacheKey(userId);
            if (cacheKey) {
                await del(cacheKey);
                
            }
        } catch (e) {
            console.error('Failed to remove cache:', e);
        }
    },
};

// ============== QUERY CLIENT ==============
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,      // 5 min fresh
            gcTime: 1000 * 60 * 60 * 24,   // 24 hr in cache
            retry: 1,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    },
});

// Auto-log HITS when data is served from memory
queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'success') {
        const query = event.query;
        // Only log as a "HIT" if it wasn't just fetched by the queryFn
        // and if it's being accessed while idle (meaning it's a sync from cache)
        if (query.state.data && query.state.fetchStatus === 'idle' && !query.state.isFetching) {
            let name = query.queryKey.flat().filter(k => typeof k === 'string').join(' > ');

            // Shorten extremely long keys (like lists of IDs)
            if (name.length > 50) name = name.substring(0, 47) + "...";

            CacheDebugger.hit(name);
        }
    }
});

// ============== CACHE MANAGEMENT ==============

/**
 * Clear current user's cache (memory + disk)
 */
export const clearUserCache = async () => {
    console.log('[Cache] 🗑️ Wiping user cache (Memory and Disk)...');
    queryClient.clear();
    await idbPersister.removeClient();
    CacheDebugger.reset();
    
};

/**
 * Clear ALL users' caches (for version upgrades)
 */
export const clearAllUsersCache = async () => {
    try {
        const allKeys = await keys();
        const cacheKeys = allKeys.filter(key =>
            typeof key === 'string' && key.startsWith('vrtta-cache-')
        );

        await Promise.all(cacheKeys.map(key => del(key)));
        queryClient.clear();
        CacheDebugger.reset();

        
    } catch (e) {
        console.error('Failed to clear all caches:', e);
    }
};

/**
 * Legacy function for backward compatibility
 */
export const clearAllCache = clearUserCache;

// Make available in browser console
if (typeof window !== 'undefined') {
    window.CacheDebugger = CacheDebugger;
    window.clearUserCache = clearUserCache;
    window.clearAllUsersCache = clearAllUsersCache;
}

export default queryClient;


